import { PencomCard } from "../cards/PencomCard";
import { formatNGN } from "@/utils/moneyFormatter";
import { getPenaltySummary } from "@/utils/penaltyBreakdown";

interface SummaryCardProps {
  label: string;
  value: string;
}

const SummaryCard = ({ label, value }: SummaryCardProps) => {
  return (
    <div className="flex flex-col gap-y-2 rounded-lg border border-stroke bg-white px-4 py-4">
      <span className="font-clash-display font-medium text-xs uppercase tracking-[0.4px] text-medium-grey">
        {label}
      </span>
      <span
        className="font-clash-display font-semibold text-[22px] tracking-[-0.084px]"
        style={{ color: "var(--color-black-dark-grey)" }}
      >
        {value}
      </span>
    </div>
  );
};

export interface PenaltySummaryNoticeProps {
  /** Raw payload from `useGetPenalties()`; the live `summary` is read off it. */
  penalties: unknown;
}

/**
 * Top-of-page penalty summary for the Penalty Breakdown screen.
 *
 * Figures come from the live `summary` object only:
 *  - TOTAL ASSESSED     = summary.totalPenalty
 *  - OUTSTANDING BALANCE = summary.totalOutstanding
 *  - ALREADY PAID        = totalPenalty − totalOutstanding (same live object)
 *
 * When nothing has been paid yet, a single Outstanding card is shown instead
 * of three (avoids the redundant "Paid 0 / New X / Balance X" layout).
 */
export const PenaltySummaryNotice = ({
  penalties,
}: PenaltySummaryNoticeProps) => {
  const summary = getPenaltySummary(penalties);
  const totalPenalty = summary?.totalPenalty ?? null;
  const totalOutstanding = summary?.totalOutstanding ?? null;

  // Nothing to show if the summary carries no figures at all.
  if (totalPenalty == null && totalOutstanding == null) return null;

  const outstanding = totalOutstanding ?? 0;
  // Fall back to outstanding when the assessed total is missing, so we never
  // show an assessed amount lower than what is owed.
  const assessed = totalPenalty ?? outstanding;
  const paid = Math.max(assessed - outstanding, 0);

  // No penalty at all — nothing to surface.
  if (assessed <= 0 && outstanding <= 0) return null;

  const hasPaid = paid > 0;

  return (
    <PencomCard className="gap-y-5">
      {/* Info notice */}
      <div className="bg-[#F9F9FB] border border-stroke rounded-lg p-4 flex flex-col gap-y-2">
        <div className="flex items-center gap-x-2">
          <img
            src="/assets/icons/info-outline.svg"
            alt=""
            className="w-4 h-4 shrink-0"
          />
          <p className="font-clash-display font-medium text-sm text-[#404040] tracking-[-0.07px]">
            Important notice about penalties
          </p>
        </div>
        <p className="font-clash-display font-normal text-sm text-[#1a1a1a] leading-[18px] tracking-[-0.07px]">
          Your contribution payment data could arrive in batches, therefore the
          figures below may not be final. When new data arrives, your statement
          is recalculated to give you a complete and accurate picture.
        </p>
        <p className="font-clash-display font-normal text-sm text-[#1a1a1a] leading-[18px] tracking-[-0.07px]">
          Outstanding penalties are to be settled through your PSSP using the
          same channel used for pension contributions.
        </p>
      </div>

      {hasPaid ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryCard
            label="Total Penalty Assessed"
            value={formatNGN(assessed)}
          />
          <SummaryCard label="You have paid" value={formatNGN(paid)} />
          <SummaryCard label="Balance to Pay" value={formatNGN(outstanding)} />
        </div>
      ) : (
        <div className="grid grid-cols-1">
          <SummaryCard label="Balance to Pay" value={formatNGN(outstanding)} />
        </div>
      )}
    </PencomCard>
  );
};
